import React, { useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";

import AppPageTitle from "../../components/AppPageTitle";
import ConfirmationModal from "../../components/ConfirmationModal";
import PermissionModal from "../../components/PermissionModal";
import WhatsAppModal from "../../components/WhatsAppModal";
import QrcodeModal from "../../components/QrcodeModal";
import ConnectionChannelsMarketplace from "./components/ConnectionChannelsMarketplace";
import ConnectionWizardModal from "./components/ConnectionWizardModal";
import HubChannelLinkModal from "./components/HubChannelLinkModal";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

function Connections() {
	const { whatsApps, loading } = useContext(WhatsAppsContext);
	const { user } = useContext(AuthContext);
	const [wizardOpen, setWizardOpen] = useState(false);
	const [wizardChannel, setWizardChannel] = useState(null);
	const [hubLinkModalOpen, setHubLinkModalOpen] = useState(false);
	const [hubLinkIntegrationSource, setHubLinkIntegrationSource] = useState("hub");
	const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
	const [qrModalOpen, setQrModalOpen] = useState(false);
	const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [permissionOpen, setPermissionOpen] = useState(false);

	const confirmationModalInitialState = {
		action: "",
		title: "",
		message: "",
		whatsAppId: "",
	};
	const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);

	const handleRequestNewQrCode = async (whatsAppId) => {
		try {
			await api.put(`/whatsappsession/${whatsAppId}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleSelectNativeChannel = (channelId) => {
		setSelectedWhatsApp(null);
		setWizardChannel(channelId);
		setWizardOpen(true);
	};

	const handleCloseWizard = useCallback((createdConnection) => {
		setWizardOpen(false);
		setWizardChannel(null);

		if (createdConnection?.channel === "whatsapp") {
			setSelectedWhatsApp(createdConnection);
			setQrModalOpen(true);
		}
	}, []);

	const handleConfigureHubChannel = useCallback((integrationSource = "hub") => {
		setHubLinkIntegrationSource(integrationSource);
		setHubLinkModalOpen(true);
	}, []);

	const handleCloseHubLinkModal = useCallback(() => {
		setHubLinkModalOpen(false);
		setHubLinkIntegrationSource("hub");
	}, []);

	const handleCloseWhatsAppModal = useCallback(() => {
		setWhatsAppModalOpen(false);
		setSelectedWhatsApp(null);
	}, []);

	const handleOpenQrModal = (whatsApp) => {
		setSelectedWhatsApp(whatsApp);
		setQrModalOpen(true);
	};

	const handleCloseQrModal = useCallback(() => {
		setSelectedWhatsApp(null);
		setQrModalOpen(false);
		window.location.reload();
	}, []);

	const handleEditWhatsApp = (whatsApp) => {
		setSelectedWhatsApp(whatsApp);
		setWhatsAppModalOpen(true);
	};

	const handleWebhook = async (whatsApp) => {
		try {
			await api.put(`/webhook/${whatsApp.id}`);
			toast.success(i18n.t("connections.toasts.webhookUpdated"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = (action, whatsAppId) => {
		if (action === "disconnect") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.disconnectTitle"),
				message: i18n.t("connections.confirmationModal.disconnectMessage"),
				whatsAppId,
			});
		}

		if (action === "delete") {
			setConfirmModalInfo({
				action,
				title: i18n.t("connections.confirmationModal.deleteTitle"),
				message: i18n.t("connections.confirmationModal.deleteMessage"),
				whatsAppId,
			});
		}

		user.profile === "admin"
			? setConfirmModalOpen(true)
			: setPermissionOpen(true);
	};

	const handleSubmitConfirmationModal = async () => {
		if (confirmModalInfo.action === "disconnect") {
			try {
				await apiV2.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
				window.location.reload();
			} catch (err) {
				toastError(err);
			}
		}

		if (confirmModalInfo.action === "delete") {
			try {
				await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
				toast.success(i18n.t("connections.toasts.deleted"));
			} catch (err) {
				toastError(err);
			}
		}

		setConfirmModalInfo(confirmationModalInitialState);
		setConfirmModalOpen(false);
	};

	return (
		<div className="app-admin-page">
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			<ConfirmationModal
				title={confirmModalInfo.title}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={handleSubmitConfirmationModal}
			>
				{confirmModalInfo.message}
			</ConfirmationModal>
			<QrcodeModal
				open={qrModalOpen}
				onClose={handleCloseQrModal}
				whatsAppId={qrModalOpen ? selectedWhatsApp?.id : undefined}
			/>
			<WhatsAppModal
				open={whatsAppModalOpen}
				onClose={handleCloseWhatsAppModal}
				whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
			/>
			<ConnectionWizardModal
				open={wizardOpen}
				onClose={handleCloseWizard}
				channelId={wizardChannel}
			/>
			<HubChannelLinkModal
				open={hubLinkModalOpen}
				onClose={handleCloseHubLinkModal}
				integrationSource={hubLinkIntegrationSource}
			/>

			<AppPageTitle
				title={i18n.t("connections.title")}
				subtitle={i18n.t("connections.subtitle")}
			/>

			<div className="app-admin-page__scroll">
				<div className="card">
					<div className="card-body">
						<ConnectionChannelsMarketplace
							connections={whatsApps}
							loading={loading}
							onSelectNativeChannel={handleSelectNativeChannel}
							onConfigureHubChannel={handleConfigureHubChannel}
							onEditConnection={handleEditWhatsApp}
							onOpenQr={handleOpenQrModal}
							onNewQr={handleRequestNewQrCode}
							onWebhook={handleWebhook}
							onDisconnect={(id) => handleOpenConfirmationModal("disconnect", id)}
							onDelete={(id) => handleOpenConfirmationModal("delete", id)}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Connections;
